import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, TrendingUp } from "lucide-react";
import { track } from "@/lib/analytics";

const money = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const Num = ({ label, value, onChange, prefix, testid }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-600">{label}</span>
    <div className="mt-1.5 relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <input
        type="number" min="0" value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
        data-testid={testid}
        className={`w-full border-2 border-slate-200 rounded-xl py-2.5 ${prefix ? "pl-7" : "pl-3"} pr-3 text-sm focus:border-blue-400 focus:outline-none`}
      />
    </div>
  </label>
);

export function EarningsCalculator() {
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const [dailyPrice, setDailyPrice] = useState(65);
  const [daysPerMonth, setDaysPerMonth] = useState(15);
  const [vehicles, setVehicles] = useState(1);
  const [delivery, setDelivery] = useState(0);
  const [mileage, setMileage] = useState(0);
  const [otherCharges, setOtherCharges] = useState(0);
  const [expenses, setExpenses] = useState(0);

  const onFirst = () => { if (!started) { setStarted(true); track("calculator_started", { page: "become_a_host" }); } };

  const rentalEarnings = (Number(dailyPrice) || 0) * (Number(daysPerMonth) || 0) * (Number(vehicles) || 0);
  const additional = (Number(delivery) || 0) + (Number(mileage) || 0) + (Number(otherCharges) || 0);
  const grossMonthly = rentalEarnings + additional;
  const grossAnnual = grossMonthly * 12;
  const net = grossMonthly - (Number(expenses) || 0);

  return (
    <div data-testid="earnings-calculator" className="bg-white rounded-3xl border border-slate-200 shadow-card overflow-hidden">
      <div className="grid md:grid-cols-2">
        <div className="p-7 sm:p-9 border-b md:border-b-0 md:border-r border-slate-100" onChange={onFirst}>
          <div className="flex items-center gap-2 text-blue-600 font-semibold"><Calculator size={18} /> Estimate your earnings</div>
          <div className="mt-6 space-y-4">
            <Num label="Your daily price" prefix="$" value={dailyPrice} onChange={setDailyPrice} testid="calc-daily" />
            <div className="grid grid-cols-2 gap-4">
              <Num label="Days rented / month" value={daysPerMonth} onChange={setDaysPerMonth} testid="calc-days" />
              <Num label="Number of vehicles" value={vehicles} onChange={setVehicles} testid="calc-vehicles" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Num label="Delivery revenue / mo" prefix="$" value={delivery} onChange={setDelivery} testid="calc-delivery" />
              <Num label="Extra-mileage / mo" prefix="$" value={mileage} onChange={setMileage} testid="calc-mileage" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Num label="Other host charges / mo" prefix="$" value={otherCharges} onChange={setOtherCharges} testid="calc-other" />
              <Num label="Your expenses / mo (optional)" prefix="$" value={expenses} onChange={setExpenses} testid="calc-expenses" />
            </div>
          </div>
        </div>

        <div className="p-7 sm:p-9 bg-slate-900 text-white flex flex-col justify-center">
          <p className="label-caps text-blue-300">Estimated gross host payout</p>
          <p data-testid="calc-monthly" className="font-heading text-4xl sm:text-5xl font-bold mt-1">{money(grossMonthly)}<span className="text-lg text-slate-400 font-medium"> /mo</span></p>
          <div className="mt-6 space-y-2.5 text-sm">
            <Row k="Listed-rate earnings" v={money(rentalEarnings)} />
            <Row k="Approved host charges" v={money(additional)} />
            <Row k="Belo host commission" v="$0" accent />
            <Row k="Estimated annual gross" v={money(grossAnnual)} bold />
            {Number(expenses) > 0 && <Row k="Est. net after your expenses" v={money(net)} />}
          </div>
          <button
            onClick={() => {
              track("calculator_completed", { page: "become_a_host", monthly: Math.round(grossMonthly), vehicles });
              track("application_started", { page: "become_a_host", location: "calculator" });
              navigate(`/host?daily=${Math.max(0, Math.round(Number(dailyPrice) || 0))}`);
            }}
            data-testid="calc-cta"
            className="mt-7 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 transition-colors rounded-full py-3 font-semibold text-white"
          >
            <TrendingUp size={17} /> Apply with this price
          </button>
          <p className="text-xs text-slate-400 mt-4 leading-relaxed">
            Estimates are educational and are not guarantees. Actual bookings, pricing, expenses, claims,
            demand, seasonality, vehicle condition, location, and availability will affect results.
          </p>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, bold, accent }) => (
  <div className="flex items-center justify-between border-t border-white/10 pt-2.5">
    <span className={`${accent ? "text-emerald-300 font-semibold" : "text-slate-300"}`}>{k}</span>
    <span className={`${bold ? "font-heading text-lg font-bold" : accent ? "text-emerald-300 font-bold" : "text-white font-semibold"}`}>{v}</span>
  </div>
);
